import type { AnswerPair } from '@/api/gen/_model/answerPair';
import type { Item } from '@/api/gen/_model/item';
import { shuffleWithoutFixedPositions } from '@/shared/quiz/answerHidingShuffle';
import { randomShuffle } from '@/shared/utils/shuffle';

/**
 * 워크시트 짝맞추기의 오른쪽 보기를, 어떤 짝도 왼쪽 짝과 같은 줄에 남지 않게 섞는다.
 *
 * 빌더는 짝을 한 줄씩 저장해 원본 순서가 곧 정답 배치다(`build/util/apiAdapter.ts`
 * `adaptMatchData`). 그대로 인쇄하면 정답이 바로 옆에 놓인다. 판정은 라벨이 아니라
 * `answerPairs` id 로 한다(라벨은 중복될 수 있다).
 *
 * 정답 페이지는 id 로 짝을 찾아 그리므로(`quizDataToAnswerPageBlock`) 여기서 섞어도 그대로 맞다.
 */
export const shuffleMatchRightItems = <T extends Item>(
  leftItems: readonly Item[],
  rightItems: readonly T[],
  answerPairs: readonly AnswerPair[] | undefined,
): T[] => {
  const partnerIdByLeftId = new Map(
    (answerPairs ?? []).map(pair => [pair.leftId, pair.rightId]),
  );
  const partnerIdsInRowOrder = leftItems.map(item =>
    partnerIdByLeftId.get(item.id),
  );
  const rightIds = rightItems.map(item => item.id);

  // 짝이 줄마다 하나씩 맞물리지 않는 데이터는 "같은 줄" 이 정의되지 않는다 — 그냥 섞는다.
  const isRowAligned =
    partnerIdsInRowOrder.length === rightIds.length &&
    partnerIdsInRowOrder.every(
      (id): id is string => id !== undefined && rightIds.includes(id),
    ) &&
    new Set(partnerIdsInRowOrder).size === rightIds.length;
  if (!isRowAligned) return randomShuffle(rightItems);

  const rightItemById = new Map(rightItems.map(item => [item.id, item]));
  return shuffleWithoutFixedPositions(
    rightIds,
    partnerIdsInRowOrder as string[],
  ).flatMap(id => rightItemById.get(id) ?? []);
};
